/**
 * Reading SkyWeather's API with a display token (api-v1 §3: `swd_…`, one site, read only).
 *
 * The kiosk URL carries the token in the fragment (`/kiosk/<siteId>#swd_…`): browsers never send a
 * fragment to a server or write it to a log, so the token only leaves the page as the
 * `Authorization` header of these requests.
 */

import type { Observation } from '@skyweather/weather-core'
import { applyQuick, FULL_RELOAD_MS, SITE_RECHECK_MS } from './refresh.js'
import type { Brand, ForecastItem, KioskData, LimitStatus, Site, SourceReading, WindsAloftItem } from './types.js'

export const API_BASE: string =
  (import.meta.env['VITE_SKYWEATHER_API'] as string | undefined) ??
  'https://vouqizlmbdmopvjwtofw.supabase.co/functions/v1/api'

/** How far back the trend line reaches: the wind chart always spans the last 6 hours (Kyle, 2026-10-24). */
export const TREND_WINDOW_MS = 6 * 60 * 60_000

/** Readings asked for per source per full load: 6 hours at one every 5 seconds, inside the API's 10,080 cap. */
export const TREND_LIMIT = 4320

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

type Fetch = (input: string, init?: RequestInit) => Promise<Response>

/**
 * What a request is signed with: a hangar-screen token (`swd_…`), or, for a signed-in member opening the
 * screen from the owner panel, a getter for their shared-login token, asked on every request so a refreshed
 * session is used. A getter that answers null means the person signed out.
 */
export type ScreenToken = string | (() => Promise<string | null>)

async function bearer(token: ScreenToken): Promise<string> {
  const t = typeof token === 'string' ? token : await token()
  if (!t) throw new ApiError(401, 'signed_out', 'Signed out.')
  return t
}

async function failure(res: Response): Promise<ApiError> {
  let code = 'http_error'
  let message = `SkyWeather answered ${res.status}`
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } }
    code = body.error?.code ?? code
    message = body.error?.message ?? message
  } catch {
    // Not JSON (a gateway error page): keep the status line.
  }
  return new ApiError(res.status, code, message)
}

/**
 * One GET. With `etag`, it is sent as If-None-Match and a 304 comes back as `null`: "nothing changed",
 * no body. `cache: 'no-store'` keeps the browser's own cache out of it, so the 304 reaches this code.
 */
async function getTagged<T>(path: string, token: ScreenToken, fetchImpl: Fetch, etag?: string | null, allowCached = false): Promise<{ body: T; etag: string | null; cachedAt: number | null } | null> {
  const headers: Record<string, string> = { authorization: `Bearer ${await bearer(token)}` }
  if (etag) headers['if-none-match'] = etag
  const res = await fetchImpl(`${API_BASE}${path}`, { headers, cache: 'no-store' })
  if (res.status === 304) return null
  if (!res.ok) throw await failure(res)
  // The service worker (`public/sw.js`) answers from its memory when the network is down, and says when that
  // answer was saved. A check must not mistake it for a live one; only a full load takes it, as "last readings".
  const offline = Number(res.headers.get('x-sw-offline'))
  const cachedAt = Number.isFinite(offline) && offline > 0 ? offline : null
  if (cachedAt !== null && !allowCached) throw new ApiError(0, 'offline', 'SkyWeather cannot be reached; showing the last readings.')
  return { body: (await res.json()) as T, etag: res.headers.get('etag'), cachedAt }
}

async function get<T>(path: string, token: ScreenToken, fetchImpl: Fetch): Promise<T> {
  const r = await getTagged<T>(path, token, fetchImpl)
  if (r === null) throw new ApiError(304, 'not_modified', 'SkyWeather answered 304 to a request without an ETag')
  return r.body
}

/** The screen's one request per check: latest readings and limit states together (`?include=limits`). */
interface Now {
  items: SourceReading[]
  limits?: LimitStatus[]
}

const nowPath = (siteId: string) => `/v1/sites/${encodeURIComponent(siteId)}/latest?include=limits`

/** Everything the screen draws, from scratch. Returns the ETag of the latest/limits answer for the next check. */
export async function loadKiosk(siteId: string, token: ScreenToken, now: number, fetchImpl: Fetch = fetch): Promise<KioskData & { etag: string | null; siteEtag: string | null }> {
  const s = encodeURIComponent(siteId)
  const [siteAnswer, nowAnswer] = await Promise.all([getTagged<Site>(`/v1/sites/${s}`, token, fetchImpl, null, true), getTagged<Now>(nowPath(siteId), token, fetchImpl, null, true)])
  if (nowAnswer === null || siteAnswer === null) throw new ApiError(304, 'not_modified', 'SkyWeather answered 304 to a request without an ETag')
  const site = siteAnswer.body
  const latest = nowAnswer.body
  const from = now - TREND_WINDOW_MS
  const trendPairs = await Promise.all(
    latest.items.map(async (r) => {
      try {
        const h = await get<{ items: Observation[] }>(
          `/v1/sources/${encodeURIComponent(r.descriptor.id)}/observations?from=${from}&to=${now}&limit=${TREND_LIMIT}`,
          token,
          fetchImpl,
        )
        return [r.descriptor.id, h.items] as const
      } catch {
        // A trend is a nicety; the panel still shows the latest reading without it.
        return [r.descriptor.id, [] as Observation[]] as const
      }
    }),
  )
  // The forecasts and winds aloft: niceties the screen can live without, so a failure gives an empty list.
  const [forecast, windsAloft] = await Promise.all([
    get<{ items: ForecastItem[] }>(`/v1/sites/${s}/forecast`, token, fetchImpl).then((r) => r.items, () => [] as ForecastItem[]),
    get<{ items: WindsAloftItem[] }>(`/v1/sites/${s}/winds-aloft`, token, fetchImpl).then((r) => r.items, () => [] as WindsAloftItem[]),
  ])
  // The customer's brand kit: a nicety like the forecasts, so a failure just leaves the SkyWeather look.
  const brand = await getTagged<{ brand: Brand | null }>(`/v1/sites/${s}/brand`, token, fetchImpl, null, true).then((r) => r?.body.brand ?? null, () => null)
  const saved = [siteAnswer.cachedAt, nowAnswer.cachedAt].filter((c): c is number => c !== null)
  const base = { site, brand, readings: latest.items, limits: latest.limits ?? [], trends: Object.fromEntries(trendPairs), forecast, windsAloft, etag: nowAnswer.etag, siteEtag: siteAnswer.etag }
  return saved.length > 0 ? { ...base, offlineSince: Math.min(...saved) } : base
}

/**
 * The live screen's loader. Between full reloads (every `FULL_RELOAD_MS`) each call is one conditional
 * request: unchanged → the same data object back (nothing redraws); changed → new readings and limit
 * states, with each new reading appended to its trend.
 */
export function createLiveLoader(siteId: string, token: ScreenToken, fetchImpl: Fetch = fetch) {
  let etag: string | null = null
  let siteEtag: string | null = null
  let fullAt = -Infinity
  let siteAt = -Infinity
  const sitePath = `/v1/sites/${encodeURIComponent(siteId)}`
  return async (now: number, prev: KioskData | null): Promise<KioskData> => {
    if (prev === null || etag === null || now - fullAt >= FULL_RELOAD_MS) {
      const { etag: tag, siteEtag: siteTag, ...data } = await loadKiosk(siteId, token, now, fetchImpl)
      etag = tag
      siteEtag = siteTag
      fullAt = data.offlineSince === undefined ? now : -Infinity
      siteAt = now
      return data
    }
    let current = prev
    // The site's own settings, every SITE_RECHECK_MS, on their own ETag. A failure here is not an outage:
    // the readings' check below decides that, and the settings are tried again next time.
    if (now - siteAt >= SITE_RECHECK_MS) {
      siteAt = now
      try {
        const site = await getTagged<Site>(sitePath, token, fetchImpl, siteEtag)
        if (site !== null) {
          siteEtag = site.etag
          current = { ...current, site: site.body }
        }
      } catch {
        // Kept the last settings; the readings' check reports reachability.
      }
    }
    const answer = await getTagged<Now>(nowPath(siteId), token, fetchImpl, etag)
    if (answer === null) {
      // Back online and nothing new: the readings on screen are current again.
      if (current.offlineSince === undefined) return current
      const { offlineSince: _gone, ...live } = current
      return live
    }
    etag = answer.etag
    return applyQuick(current, answer.body.items, answer.body.limits ?? [], now - TREND_WINDOW_MS)
  }
}
